<script setup lang="ts">
import {computed, onBeforeUpdate, onMounted, ref} from "vue";
import {IonIcon} from "@ionic/vue";
import {needsAction, useAInesObjectiveStore} from "@/helper/chat/ainesObjectives";
import {AInesObjective, AInesObjectiveState, AInesObjectiveSubject} from "@/graphql/generated/graphql";
import RichResponseListItem from "./RichResponses/components/RichResponseListItem.vue";
import date from "@/helper/datetime/date";
import Skeleton from "@/components/Skeleton.vue";
import {iconType2Icon} from "@/helper/icon";
import {getVerticalDifferencInPx, isElementBottomOutOfBoundsInScrollContainer} from '@/helper/dom'
import Transitionator from "../components/transitions/Transitionator.vue";
import CalendarDetail from "@/views/CalendarDetail/CalendarDetail.vue";
import {kapitelDateString} from "@/graphql/kapitelTypes";
import {consoleErrorApp} from "@/helper/console";

const props = defineProps({
    blur: {
        type: Boolean,
        required: false,
        default: false
    }
})


const emit = defineEmits(["objective:click", "bookingSuggestion:open"]);
const isLoading = computed(() => store.loading)
const itemRefs = ref([] as any[])
const isActionNeededScrollHintVisible = ref(false)
const scrollElement = ref()
const dayDetailDate = ref<kapitelDateString | undefined>(undefined)

const setItemRef = (comp:any) => comp ? itemRefs.value.push(comp) : void 0

const store = useAInesObjectiveStore();

const showProgress = (objective: AInesObjective) => {
    const progress = store.planningObjectivesProgress?.get(objective.reference)
    let completed = false;
    if (progress && progress==1.0) {
        completed = true;
    }
    return objective.subject === AInesObjectiveSubject.Planning &&
        objective.state === AInesObjectiveState.InProgress &&
        !completed;
}

onMounted(async () => {
    await store.refetch();

    requestAnimationFrame(() => {
        updateNeedsActionScrollHint();
    })
});

onBeforeUpdate(() => {
    itemRefs.value = []
})

enum GroupTitle {
    direktanfragen = 'Direktanfragen',
    zeiterfassung = 'Zeiterfassung',
    abrechnung = 'Abrechnung',
    erste = 'Erste Schritte',
    dienstplanung = 'Dienstplanung',
    diensttausch = 'Diensttausch',
    eingetragene = 'Eingetragene Dienste',
    anfragen = 'Anfragen ans IBFG Team'
}

const objectiveSubjectTitlesMap = {
    [AInesObjectiveSubject.Payroll]: 'Abrechnung',
    [AInesObjectiveSubject.Planning]: 'Dienstplanung',
    [AInesObjectiveSubject.Timesheet]: 'Zeiterfassung',
    [AInesObjectiveSubject.DirectDemand]: 'Direktanfragen',
    [AInesObjectiveSubject.RegisterBooking]: 'Eingetragene Dienste',
    [AInesObjectiveSubject.CandidateDemand]: 'Erste Schritte',
    [AInesObjectiveSubject.CandidateJobsites]: 'Erste Schritte',
    [AInesObjectiveSubject.OnboardingConfirmShiftTimes]: 'Erste Schritte',
    [AInesObjectiveSubject.OnboardingInitializeJobsites]: 'Erste Schritte',
    [AInesObjectiveSubject.OnboardingStartPlanning]: 'Erste Schritte',
    [AInesObjectiveSubject.HumanRequest]: 'Anfragen ans IBFG Team',
    [AInesObjectiveSubject.OnboardingSedcard]: 'Erste Schritte',
    [AInesObjectiveSubject.BookingWithdrawalRequest]: 'Diensttausch',
    [AInesObjectiveSubject.BookingSuggestion]: 'Diensttausch',
    default: 'Sonstiges',
}

type Group = { subject: AInesObjectiveSubject, title: GroupTitle, objectives: AInesObjective[] }

enum GroupPrio {
    urgent,
    todo_high,
    todo_low,
    status_high,
    status_low
}

const groupTitleOrderIndex = {
    [GroupTitle.direktanfragen]: GroupPrio.urgent,
    [GroupTitle.zeiterfassung]: GroupPrio.todo_high,
    [GroupTitle.abrechnung]: (group: Group) => group.objectives.some(needsAction) ? GroupPrio.urgent : GroupPrio.status_low,
    [GroupTitle.erste]: GroupPrio.todo_low,
    [GroupTitle.dienstplanung]: (group: Group) => group.objectives.some(needsAction) ? GroupPrio.todo_high : GroupPrio.status_high,
    [GroupTitle.diensttausch]: (group: Group) => group.objectives.some(needsAction) ? GroupPrio.urgent : GroupPrio.status_high,
    [GroupTitle.eingetragene]: GroupPrio.todo_low,
    [GroupTitle.anfragen]: (group: Group) => group.objectives.some(needsAction) ? GroupPrio.todo_low : GroupPrio.status_low,
} as {[key in GroupTitle] : (number | ((group:Group) => number))}

const objectiveSubjectToGroupTitleMap = {
    [AInesObjectiveSubject.Payroll]: GroupTitle.abrechnung,
    [AInesObjectiveSubject.Planning]: GroupTitle.dienstplanung,
    [AInesObjectiveSubject.Timesheet]: GroupTitle.zeiterfassung,
    [AInesObjectiveSubject.DirectDemand]: GroupTitle.direktanfragen,
    [AInesObjectiveSubject.RegisterBooking]: GroupTitle.eingetragene,
    [AInesObjectiveSubject.BookingSuggestion]: GroupTitle.diensttausch,
    [AInesObjectiveSubject.BookingWithdrawalRequest]: GroupTitle.diensttausch,
    [AInesObjectiveSubject.CandidateDemand]: GroupTitle.erste,
    [AInesObjectiveSubject.CandidateJobsites]: GroupTitle.erste,
    [AInesObjectiveSubject.OnboardingConfirmShiftTimes]: GroupTitle.erste,
    [AInesObjectiveSubject.OnboardingInitializeJobsites]: GroupTitle.erste,
    [AInesObjectiveSubject.OnboardingStartPlanning]: GroupTitle.erste,
    [AInesObjectiveSubject.HumanRequest]: GroupTitle.anfragen,
    [AInesObjectiveSubject.OnboardingSedcard]: GroupTitle.erste
} as {[key in AInesObjectiveSubject] : GroupTitle}

const sortGroups = (groups: Group[]) => {
    const getTitleIndex = (group: Group) => {
        const idx = groupTitleOrderIndex[group.title] // as (number | (group:Group) => number);
        return (typeof idx === "number") ? idx : idx(group)
    }
    groups.sort((g1, g2) => getTitleIndex(g1) < getTitleIndex(g2) ? -1 : 1)
}

const sortGroupContentDependingOnSubject = (group: Group) => {
    switch (group.subject) {
        case AInesObjectiveSubject.Planning:
            return {
                ...group,
                objectives: group.objectives.sort((a, b) => date.cmpKapitelDate(a.reference as kapitelDateString, b.reference as kapitelDateString))
            }
        case AInesObjectiveSubject.Timesheet:
            return {
                ...group,
                objectives: group.objectives.sort((a, b) => date.cmpKapitelDate(
                    a.reference as kapitelDateString,
                    b.reference as kapitelDateString
                    )
                )
            }
        case AInesObjectiveSubject.BookingSuggestion:
        case AInesObjectiveSubject.BookingWithdrawalRequest:
            return {
                ...group,
                objectives: group.objectives.sort((a, b) => a.reference.localeCompare(b.reference))
            }
        case AInesObjectiveSubject.RegisterBooking:
            return {
                ...group,
                objectives: group.objectives.sort((a, b) => date.cmpKapitelDate(
                    a.reference as kapitelDateString,
                    b.reference as kapitelDateString
                    )
                )
            }
        // todo: dringende nach oben!
        // case AInesObjectiveSubject.HumanSupport:
        //     return {
        //         ...group,
        //         objectives: group.objectives.sort((a, b) => a.needsAction ? -1 : 1)
        //     }
        default:
            return group
    }
}

const handleObjectiveClick = (objective: AInesObjective) => {
    if (objective.clickAction?.type === 'openModal') {
        if (objective.subject === AInesObjectiveSubject.BookingSuggestion) {
            emit('bookingSuggestion:open')
        } else {
            consoleErrorApp("unknown objective modal " + objective.subject)
        }
    }else {
        emit('objective:click', objective)
    }
}


const objectivesGrouped = computed(() => {
    const groups = Object.values(store.objectives.reduce((res, objective) => {
        const title = objectiveSubjectToGroupTitleMap[objective.subject]
        if (!title) {
            debugger
            console.warn('missing title for objective subject %o', objective.subject)
            return res
        }
        if (!res[title]) {
            res[title] = {
                subject: objective.subject,
                title: title,
                objectives: [] as AInesObjective[]
            } as Group
        }

        res[title].objectives.push(objective)

        return res
    }, {} as {[key: string]: Group}))

    sortGroups(groups)

    groups.map(sortGroupContentDependingOnSubject)

    return groups
})

const getFirstInVisibleNeedsActionItem = () => {
    return itemRefs.value.find(itemComponent => {
        return itemComponent.props?.badge &&
            isElementBottomOutOfBoundsInScrollContainer(itemComponent.$el, scrollElement.value as HTMLElement)
    })
}

const updateNeedsActionScrollHint = () => {
    const isVisible = getFirstInVisibleNeedsActionItem() !== undefined
    isActionNeededScrollHintVisible.value = isVisible
}

const onClickScrollToAction = () => {
    const item = getFirstInVisibleNeedsActionItem()

    if (!item) {
        return
    }

    const delta = getVerticalDifferencInPx(item.$el, scrollElement.value)
    scrollElement.value.scrollTo({top: scrollElement.value.scrollTop + delta, behavior: 'smooth'});
}

defineExpose({
    updateNeedsActionScrollHint
})

</script>

<template>
    <div class="aines-container transparent no-padding-top no-padding-bottom" :class="{blur: props.blur}">
        <div ref="scrollElement" class="scroller app-padding-left app-padding-right" @scroll="updateNeedsActionScrollHint" >
            <h1 class="custom-ricarda">
                
                Aktuelles
                <img src="/logo.png" />
            </h1>
            <Skeleton
                v-if="isLoading"
                :items="['margin', 'title', 'block--height-6em', 'block--height-6em', 'block--height-6em']"
            />
            <template v-else>
                <template v-for="group in objectivesGrouped">
                    <h2 class="globalHeader" :data-cy="'objective '+ group.title">{{ group.title }}</h2>
                    <RichResponseListItem
                        v-for="objective in group.objectives"
                        :key="objective.reference"
                        :title="objective.title"
                        :text="objective.text"
                        :icon-type="showProgress(objective) ? 'progress' : iconType2Icon(objective.icon).iconType"
                        :icon="showProgress(objective)? store.planningObjectivesProgress?.get(objective.reference) : iconType2Icon(objective.icon).iconId"
                        :icon-color="objective.needsAction ? (objective.state == AInesObjectiveState.CompletedSuccessfully ? 'success': 'primary') : undefined"
                        @click="handleObjectiveClick(objective)"
                        :type="objective.needsAction ? (objective.state == AInesObjectiveState.CompletedSuccessfully ? 'success': 'primary') : undefined"
                        :badge="objective.needsAction"
                        :ref="setItemRef"
                        badgeValue="!"
                    >
                        <!--<template v-slot:end>
                            <ProgressCircle v-if="objective.subject===AInesObjectiveSubject.Planning & objective.state == AInesObjectiveState.InProgress"
                                class="listItemSuffixImage"
                                :stroke-width="15"
                                track-color="grey-tint-1"
                                :stroke-color="objective.needsAction ? 'primary-tint' : 'grey-tint-1'"
                                :glow="false"
                                :value=""
                            />
                        </template>-->
                    </RichResponseListItem>
                </template>
            </template>
        </div>
        <Transitionator :types="['opacity', 'scale']" easing="easeOutBounce">
            <div
                v-if="isActionNeededScrollHintVisible"
                class="needs-action-hint-button floating-big-pseudo overlaying-other-elements"
                @click="onClickScrollToAction"
                style="transform: translate(-50%, -4em)"
            >
                Weitere
                <ion-icon src="/icons/caret-down.svg" />
            </div>
        </Transitionator>
        <CalendarDetail v-if="dayDetailDate" v-model="dayDetailDate" />
    </div>
</template>

<style scoped lang="scss">
.aines-container {
    transition: filter 0.3s;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    
    > .scroller {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        overflow-x: hidden;
        overflow-y: auto;

        padding-bottom: 8em;

    }

    .needs-action-hint-button {
        position: absolute;
        top: 100%;
        left: 50%;
        background-color: var(--ion-color-red-tint-3);
        color: var(--ion-color-primary);
        z-index: 300;
        // transform: translate(-50%, -4em);
        padding: 0.5em;
        border-radius: 1em;
        font-weight: var(--custom-font-weight-medium);
        display: flex;
        align-items: center;

        ion-icon {
            margin-left: 0.3em;
        }
    }

    h1 {
        margin-bottom: 0.5em;
        display: flex;
        align-items: center;
        margin-left: -0.1em;
        margin-top: 0.25em;

        img {
            $SIZE: 0.6em;
            width: $SIZE;
            height: $SIZE;
            margin-left: 0.15em;
            margin-right: 0.25em;
            margin-top: -0.5em;
        }
    }

    ion-item {
        margin-bottom: 1em;
    }

    &.blur {
        filter: blur(0.5em);

        &,
        * {
            pointer-events: none;
        }
    }
}


</style>
